import { useCallback, useEffect, useRef, useState } from "react";
import type { MetricsResponse } from "@shared/types";
import { fetchMetrics, ApiError } from "./api-client";

interface UseMetricsState {
  data: MetricsResponse | null;
  error: ApiError | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

const FIVE_MINUTES = 5 * 60 * 1000;

export function useMetrics(pollMs: number = FIVE_MINUTES): UseMetricsState {
  const [data, setData] = useState<MetricsResponse | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const mountedRef = useRef<boolean>(true);

  const load = useCallback(async () => {
    try {
      const result = await fetchMetrics();
      if (!mountedRef.current) return;
      setData(result);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      if (err instanceof ApiError) setError(err);
      else setError(new ApiError(0, "unknown", "unknown error"));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void load();
    const id = window.setInterval(() => {
      void load();
    }, pollMs);
    return () => {
      mountedRef.current = false;
      window.clearInterval(id);
    };
  }, [load, pollMs]);

  return { data, error, loading, refetch: load };
}
